/**
 * SiteMapSidePanel: the text side of the site map. Fully usable without the map: site search,
 * freshness, fleet totals, the current selection, sites needing attention and the
 * partial-index notice.
 */
import { useMemo } from 'react';
import type { SiteMapEntry, SiteMapSelection } from '../models/siteMapModels';
import type { HealthState } from '../hooks/siteMapState';
import { attentionList, describeFreshness, summarise } from '../utils/siteMapPresentation';
import { HealthSummary } from './HealthSummary';
import { AttentionList } from './AttentionList';
import { SiteCard } from './SiteCard';
import { PartialIndexNotice } from './PartialIndexNotice';
import { SiteSearch } from './SiteSearch';

interface SiteMapSidePanelProps {
  readonly entries: readonly SiteMapEntry[];
  readonly health: HealthState;
  readonly recentShotsWindowS: number;
  readonly now: Date;
  readonly selection: SiteMapSelection;
  readonly searchString: string;
  /** Site search query to start with; read on mount */
  readonly initialSearchQuery: string;
  readonly onSearchQueryChange: (query: string) => void;
  readonly onShowOnMap: (siteId: string) => void;
  readonly onOpen: (siteId: string) => void;
  readonly onClearSelection: () => void;
}

function selectedEntries(
  entries: readonly SiteMapEntry[],
  selection: SiteMapSelection
): readonly SiteMapEntry[] {
  if (!selection) return [];
  const ids = selection.kind === 'site' ? [selection.siteId] : selection.siteIds;
  const byId = new Map(entries.map((entry) => [entry.site.siteId, entry]));
  return ids.map((id) => byId.get(id)).filter((e): e is SiteMapEntry => e !== undefined);
}

export function SiteMapSidePanel({
  entries,
  health,
  recentShotsWindowS,
  now,
  selection,
  searchString,
  initialSearchQuery,
  onSearchQueryChange,
  onShowOnMap,
  onOpen,
  onClearSelection,
}: SiteMapSidePanelProps): JSX.Element {
  const summary = useMemo(() => summarise(entries), [entries]);
  const attention = useMemo(() => attentionList(entries), [entries]);
  const selected = useMemo(() => selectedEntries(entries, selection), [entries, selection]);
  const freshness = describeFreshness(health, now);

  return (
    <div className="site-map-panel">
      <SiteSearch
        entries={entries}
        initialQuery={initialSearchQuery}
        onPick={onShowOnMap}
        onQueryChange={onSearchQueryChange}
      />

      <p
        className={`site-map-panel__freshness site-map-panel__freshness--${freshness.tone}`}
        role="status"
      >
        {freshness.text}
      </p>

      <HealthSummary summary={summary} recentShotsWindowS={recentShotsWindowS} />

      {selected.length > 0 && (
        <section className="site-map-panel__section" aria-labelledby="site-map-selection-heading">
          <div className="site-map-panel__section-header">
            <h2 id="site-map-selection-heading" className="site-map-panel__heading">
              {selected.length === 1 ? 'Selected site' : `${selected.length} sites at this location`}
            </h2>
            <button type="button" className="site-map-button site-map-button--link" onClick={onClearSelection}>
              Clear
            </button>
          </div>
          <div className="site-map-panel__cards">
            {selected.map((entry) => (
              <SiteCard
                key={entry.site.siteId}
                entry={entry}
                now={now}
                recentShotsWindowS={recentShotsWindowS}
                searchString={searchString}
                onOpen={onOpen}
              />
            ))}
          </div>
        </section>
      )}

      <AttentionList
        entries={attention}
        searchString={searchString}
        onShowOnMap={onShowOnMap}
        onOpen={onOpen}
      />

      <PartialIndexNotice searchString={searchString} />
    </div>
  );
}
